import React, { useState } from 'react'
import OnboardingTasksDrawer from '../components/OnboardingTasksDrawer'
import Text from 'core-system/Text'
import RadioV2 from 'core-system/RadioV2'
import Divider from 'core-system/Divider'
import palette from 'core-system/Themes/palette'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import RandomUtils from 'shared/RandomUtils'
import { FinchWorksite } from 'redux/employer/employerTypes'
import { employerActions } from 'redux/employer/employerSlice'
import { employeeActions } from 'redux/employee/employeeSlice'
import employerService from 'redux/employer/employerService'
import { useGateValue } from '@statsig/react-bindings'
import { onboardingActions } from 'features/Onboarding/onboardingActions'

const RadioListContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const FinchWorksitesContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-bottom: 0.5rem;
  gap: 1rem;
  scrollbar-width: none;
`

const FinchWorksiteRadioItemLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

interface WorksiteSyncDrawerProps {
  isOpen: boolean
  onClose: () => void
}
const WorksiteSyncDrawer = (props: WorksiteSyncDrawerProps) => {
  const { isOpen, onClose } = props
  const dispatch = useDispatch()
  const { allFinchWorksites } = useSelector((state: AppState) => state.employer)
  const [selectedWorksites, setSelectedWorksites] = useState<Set<string>>(
    new Set()
  )
  const { onboarding } = useSelector((state: AppState) => state.onboarding)
  const [isLoading, setIsLoading] = useState(false)
  const isConnectedAccountEnabled = useGateValue('connected_account')

  const handleWorksiteRadioClick = (worksiteId: string) => {
    if (worksiteId === 'all') {
      const syncableFinchWorksites = allFinchWorksites.filter(
        (worksite) => worksite.canSync
      )
      if (selectedWorksites.size < syncableFinchWorksites.length) {
        setSelectedWorksites(
          new Set(syncableFinchWorksites.map((worksite) => worksite.id))
        )
      } else {
        setSelectedWorksites(new Set())
      }
    } else if (selectedWorksites.has(worksiteId)) {
      setSelectedWorksites((prev) => {
        prev.delete(worksiteId)
        return new Set(prev)
      })
    } else {
      setSelectedWorksites((prev) => new Set(prev.add(worksiteId)))
    }
  }

  const renderSyncableFinchWorksites = (allFinchWorksites: FinchWorksite[]) => {
    return allFinchWorksites
      .filter((worksite) => worksite.canSync)
      .map((worksite) => (
        <RadioV2
          key={worksite.id}
          active={selectedWorksites.has(worksite.id)}
          label={
            <FinchWorksiteRadioItemLabel>
              <Text variant='action3'>{worksite.address}</Text>
              <Text variant='body1'>
                {`(${
                  worksite.unsyncedEmployees || 0
                } Unsynced Employee${RandomUtils.pluralCheck(
                  worksite.unsyncedEmployees
                )},
                  ${
                    worksite.unsyncedContractors || 0
                  } Unsynced Contractor${RandomUtils.pluralCheck(
                    worksite.unsyncedContractors
                  )})`}
              </Text>
            </FinchWorksiteRadioItemLabel>
          }
          onClick={() => handleWorksiteRadioClick(worksite.id)}
          marginY='0.25rem'
        />
      ))
  }

  const handleIntegrateWorksites = () => {
    if (allFinchWorksites?.length === 0) {
      dispatch(
        onboardingActions.updateOnboardingSectionStatus({
          sectionType: 'WORKSITE_SYNC',
          status: 'COMPLETED',
        })
      )
      onClose()
      return
    }
    setIsLoading(true)
    employerService
      .bulkSyncFinchWorksiteEmployees(Array.from(selectedWorksites))
      .then((res) =>
        dispatch(
          employerActions.bulkSyncFinchWorksiteEmployeesSuccess(res.data)
        )
      )
      .then(() => {
        // Only getting connected person if connected account is enabled
        // and workforceSyncType is HRIS
        if (
          isConnectedAccountEnabled &&
          onboarding.workforceSyncType === 'HRIS'
        ) {
          dispatch(employerActions.getConnectedPersons())
        }
      })
      .then(() => dispatch(employeeActions.getCommuters()))
      .then(() => dispatch(employerActions.getWorksites()))
      .then(() => {
        dispatch(
          onboardingActions.updateOnboardingSectionStatus({
            sectionType: 'WORKSITE_SYNC',
            status: 'COMPLETED',
          })
        )
        setIsLoading(false)
        onClose()
      })
  }

  const buttonText =
    allFinchWorksites?.length === 0 ? 'Complete' : 'Integrate Worksites'

  const buttonDisabled =
    allFinchWorksites?.length === 0 ? false : selectedWorksites.size === 0

  return (
    <OnboardingTasksDrawer
      isOpen={isOpen}
      onClose={onClose}
      primaryButton={{
        text: buttonText,
        onClick: () => handleIntegrateWorksites(),
        disabled: buttonDisabled,
        loading: isLoading,
      }}
    >
      <Text variant='h3' marginBottom='2rem'>
        Worksite Sync
      </Text>
      {allFinchWorksites?.length > 0 && (
        <RadioListContainer>
          <RadioV2
            key='all'
            active={selectedWorksites.size === allFinchWorksites.length}
            label={
              <Text variant='action4' color={palette.primary.pink500}>
                Select All
              </Text>
            }
            onClick={() => handleWorksiteRadioClick('all')}
            marginY='0.25rem'
          />
          <Divider />
          <FinchWorksitesContainer>
            {renderSyncableFinchWorksites(allFinchWorksites)}
          </FinchWorksitesContainer>
        </RadioListContainer>
      )}
      {allFinchWorksites?.length === 0 && (
        <Text variant='body1' marginBottom='2rem'>
          All your worksites are already integrated.
        </Text>
      )}
    </OnboardingTasksDrawer>
  )
}

export default WorksiteSyncDrawer
